import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import * as M from "./styles";
import { useGetTableData2 } from "./tableHooks";
import { CirclesWithBar } from "react-loader-spinner";
import { useColumns } from "./useColumns";
import { useTableInstance } from "./useTableInstance";
import CreateRowDialog from "./CreateRowDialog";
import { useQueryClient } from "@tanstack/react-query";
import { useScheduling } from "./useScheduling";
import { useTableOperations } from "./useTableOperations";
import { useEngineStatus } from "./useEngineStatus";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { handleSchedulingStatus } from "../../../slice/learnSlice";
import {
  Button,
  Modal,
  Box,
  Alert,
  Snackbar,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add"; // ✅ 추가
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import { toast } from "react-toastify";
import BulkEditDialog from "./BulkEditDialog";
import useInformationData from "../hooks";
import EditQtyDialog from "./EditQtyDialog";
import CreateStockDialog from "./CreateStockDialog"; // ✅ 추가

function formatTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours === 0 && minutes === 0) {
    return `${seconds}초`;
  }
  if (hours === 0) {
    return `${minutes}분 ${seconds}초`;
  }
  return `${hours}시 ${minutes}분 ${seconds}초`;
}

const useStockColumns = () => {
  return useMemo(
    () => [
      {
        accessorKey: "index",
        header: "순번",
        size: 60,
        enableSorting: false,
        enableColumnActions: false,
        enableEditing: false,
        Cell: ({ row }) => {
          return (
            <Box sx={{ textAlign: "center", fontWeight: 600 }}>
              {row.index + 1}
            </Box>
          );
        },
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "item_code",
        header: "고객사 품번",
        size: 150,
        enableEditing: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: {
          align: "center",
          sx: { backgroundColor: "#f5f5f5" },
        },
      },
      {
        accessorKey: "material_grouping",
        header: "사내 품명",
        size: 200,
        enableEditing: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "car_type",
        header: "차종",
        size: 120,
        enableEditing: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "spec",
        header: "규격",
        size: 150,
        enableEditing: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "required_date_str",
        header: "필요 일자",
        size: 130,
        enableEditing: true,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value || "-";
        },
      },
      {
        accessorKey: "required_quantity",
        header: "필요 수량",
        size: 130,
        enableEditing: true,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value?.toLocaleString() || "0";
        },
      },
      {
        accessorKey: "result_status",
        header: "수립결과",
        size: 100,
        enableEditing: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell, row }) => {
          const status = row.original.status;
          const resultStatus = cell.getValue();
          if (status === 0 || status === null || status === undefined) {
            return <Chip label="대기" size="small" color="default" />;
          }
          if (resultStatus === 1) {
            return <Chip label="충분" size="small" color="success" />;
          } else if (resultStatus === 0) {
            return <Chip label="부족" size="small" color="error" />;
          }
          return <Chip label="대기" size="small" color="default" />;
        },
      },
    ],
    []
  );
};

// AI 생산계획 페이지
function Plan() {
  const [updateStatus, setUpdateStatus] = useState(false);
  const [updateStatus2, setUpdateStatus2] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateValue, setDateValue] = useState(null);
  const [editQtyModalOpen, setEditQtyModalOpen] = useState(false);
  const [editQtyRowData, setEditQtyRowData] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [scheOptions, setScheOptions] = useState(null);
  const [lastOperationTime, setLastOperationTime] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { group_code, project_code } = useParams();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 11,
  });
  const [tableDataWithExcluded, setTableDataWithExcluded] = useState([]);
  const [selectionLock, setSelectionLock] = useState(false);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  
  // ✅ 추가 모달 state
  const [addModalOpen, setAddModalOpen] = useState(false);

  const handleOpenEditQtyModal = useCallback((rowData) => {
    setEditQtyRowData(rowData);
    setEditQtyModalOpen(true);
  }, []);

  const handleCloseEditQtyModal = useCallback(() => {
    setEditQtyModalOpen(false);
    setEditQtyRowData(null);
  }, []);

  const skipNextClickRef = useRef(false);

  useEffect(() => {
    if (!selectionLock) return;
    window.getSelection?.().removeAllRanges?.();
    const onMouseUp = () => {
      setSelectionLock(false);
      window.getSelection?.().removeAllRanges?.();
      skipNextClickRef.current = true;
      setTimeout(() => (skipNextClickRef.current = false), 0);
    };
    document.addEventListener("mouseup", onMouseUp, { once: true });
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, [selectionLock]);

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleDateChange = (newValue) => {
    if (newValue && newValue.isValid && newValue.isValid()) {
      setDateValue(newValue);
    } else if (newValue === null) {
      setDateValue(null);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const isCancelled = useRef(false);
  const dispatch = useDispatch();
  const { isScheduling, processStatus } = useSelector((state) => state.learn);
  const queryClient = useQueryClient();

  // ✅ 재고 정보 데이터 가져오기
  const { data, isFetching, refetch } = useGetTableData2(
    updateStatus,
    `/api/tpc/getStockInfoLog?group_code=${group_code}&project_code=${project_code}`
  );

  // ✅ 엔진 상태 조회
  const {
    engineStatus,
    loading: engineLoading,
    refetchEngineStatus,
  } = useEngineStatus(group_code, project_code, isScheduling);

  // ✅ 검색 필터링
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchText) return data;
    const searchTermLower = searchText.toLowerCase();
    return data.filter((row) => {
      return Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(searchTermLower)
      );
    });
  }, [data, searchText]);

  const onChangeSearchValue = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  const { infoData } = useInformationData(group_code, project_code);
  const { item } = infoData;

  const {
    doSchedulingMutation,
    handleCreateOrder: baseHandleCreateOrder,
    handleDeleteSelected: baseHandleDeleteSelected,
    handleUpdateOrder: baseHandleUpdateOrder,
  } = useTableOperations(updateStatus, openSnackbar, group_code, project_code);

  const handleCreateOrder = (params) =>
    baseHandleCreateOrder(params, setUpdateStatus);
  const handleDeleteSelected = (table) =>
    baseHandleDeleteSelected(table, setUpdateStatus);
  const handleUpdateOrder = (params) => {
    baseHandleUpdateOrder(params, setUpdateStatus);
  };
  const fetchLastOperationTime = useCallback(async () => {
    try {
      const res = await axios.get(
        `${TPC_URL}/api/tpc/getOperationLast?group_code=${group_code}&project_code=${project_code}`
      );
      
      if (res.data && res.data.length > 0 && res.data[0].operation_time) {
        setLastOperationTime(res.data[0].operation_time);
      }
    } catch (error) {
      console.error("Error fetching operation time:", error);
    }
  }, [group_code, project_code, updateStatus2]);

  const { handleDoScheduling, handleCancelScheduling } = useScheduling(
    dispatch,
    isCancelled,
    openSnackbar,
    setUpdateStatus,
    group_code,
    project_code,
    setLoading,
    setLoadingMessage,
    loadingMessage,
    dateValue,
    scheOptions,
    setUpdateStatus2
  );

  const updateRowExcludedStatus = useCallback((orderId, isExcluded) => {
    setTableDataWithExcluded((prev) =>
      prev.map((row) =>
        row.order_id === orderId ? { ...row, is_excluded: isExcluded } : row
      )
    );
  }, []);
  useEffect(() => {
    async function fetchScheOptions() {
      try {
        const res = await axios.get(
          `${TPC_URL}/api/tpc/getTestSchedulingOption?group_code=${group_code}&project_code=${project_code}`
        );
        setScheOptions(res.data.status);
        await fetchLastOperationTime();
      } catch (error) {
        console.error("Error fetching sche options:", error);
      }
    }
    fetchScheOptions();
  }, [group_code, project_code, fetchLastOperationTime]);

  const handleGeneratePlan = async() => {
    try { 
      setLoading(true);
      const res = await axios.get(`${TPC_URL}/api/tpc/generatePlan?group_code=${group_code}&project_code=${project_code}`);
      if (res.status === 200) {
        openSnackbar("생산계획 수립되었습니다.", "success");
        setUpdateStatus((prev) => !prev);
        await refetch()
      } else {
        openSnackbar(res.data.message || "생산계획 수립 실패", "error");
      }
    } catch (error) {
      console.error("Error generating plan:", error);
      openSnackbar("생산계획 수립 중 오류가 발생했습니다.", "error");
    }
    finally {
      setLoading(false);
      setUpdateStatus((prev) => !prev);
    }
  }

  // ✅ 재고 정보 컬럼 사용
  const stockColumns = useStockColumns();

  // ✅ 엔진 초기화 다이얼로그 열기
  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
  };

  // ✅ 엔진 초기화 다이얼로그 닫기
  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
  };

  // ✅ 엔진 초기화 실행
  const handleConfirmReset = async () => {
    try {
      const res = await axios.post(`${TPC_URL}/api/tpc/resetEngine`, {
        group_code,
        project_code,
      });
      if (res.data.success) {
        openSnackbar("엔진이 초기화되었습니다", "success");
        refetchEngineStatus();
      } else {
        openSnackbar(res.data.message || "엔진 초기화 실패", "error");
      }
    } catch (error) {
      console.error("[ERROR] resetEngine:", error);
      openSnackbar("엔진 초기화 중 오류가 발생했습니다", "error");
    } finally {
      handleCloseResetDialog();
    }
  };

  // ✅ 추가 모달 열기/닫기
  const handleOpenAddModal = () => {
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

// ✅ 재고 정보 추가 저장
const handleSaveNewStock = async (formData) => {
  try {
    setLoading(true);

    await axios.post(`${TPC_URL}/api/stock/insertStockInfo`, {
      group_code,
      project_code,
      data: [
        {
          item_id: formData.item_id,
          required_date: formData.required_date.format("YYYY-MM-DD"),
          required_quantity: formData.required_quantity,
        },
      ],
    });

    openSnackbar("추가되었습니다.", "success");
    await refetch();
    handleCloseAddModal();
  } catch (error) {
    console.error("Error adding stock info:", error);
    openSnackbar("추가 중 오류가 발생했습니다.", "error");
  } finally {
    setLoading(false);
  }
};

  // ✅ handleSaveRow
  const handleSaveRow = async ({ exitEditingMode, row, values }) => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!values.required_date_str || !dateRegex.test(values.required_date_str)) {
      openSnackbar("올바른 날짜 형식(YYYY-MM-DD)을 입력해주세요.", "error");
      return;
    }

    const date = new Date(values.required_date_str);
    if (isNaN(date.getTime())) {
      openSnackbar("유효하지 않은 날짜입니다.", "error");
      return;
    }

    const quantity = parseInt(values.required_quantity);
    if (isNaN(quantity) || quantity < 0) {
      openSnackbar("올바른 수량을 입력해주세요. (0 이상)", "error");
      return;
    }

    try {
      setLoading(true);

      const updatedData = {
        info_id: row.original.info_id,
        item_id: row.original.item_id,
        required_date: values.required_date_str,
        required_quantity: quantity,
      };

      // ✅ 낙관적 업데이트
      queryClient.setQueryData(
        ["stockInfoLog", group_code, project_code],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((item) =>
            item.info_id === row.original.info_id
              ? {
                  ...item,
                  required_date_str: values.required_date_str,
                  required_quantity: quantity,
                }
              : item
          );
        }
      );

      await axios.put(`${TPC_URL}/api/stock/updateStockInfo`, {
        group_code,
        project_code,
        data: [updatedData],
      });

      openSnackbar("수정되었습니다.", "success");

      // ✅ 백그라운드 동기화
      queryClient.invalidateQueries({
        queryKey: ["stockInfoLog", group_code, project_code],
      });

      exitEditingMode();
    } catch (error) {
      console.error("Error updating stock info:", error);
      openSnackbar("수정 중 오류가 발생했습니다.", "error");

      // ✅ 에러 시 롤백
      queryClient.invalidateQueries({
        queryKey: ["stockInfoLog", group_code, project_code],
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ handleDeleteRows
  const handleDeleteRows = async () => {
    const selectedRowsData = Object.keys(rowSelection).map((key) => {
      return filteredData[parseInt(key)];
    });

    if (selectedRowsData.length === 0) {
      openSnackbar("삭제할 행을 선택해주세요.", "warning");
      return;
    }

    const idsToDelete = selectedRowsData
      .filter((row) => row && row.info_id)
      .map((row) => row.info_id);

    if (idsToDelete.length === 0) {
      openSnackbar("삭제할 수 있는 데이터가 없습니다.", "warning");
      return;
    }

    try {
      setLoading(true);

      await axios.delete(
        `${TPC_URL}/api/stock/deleteStockInfo?group_code=${group_code}&project_code=${project_code}&ids=${idsToDelete.join(
          ","
        )}`
      );

      openSnackbar(`${idsToDelete.length}개 행이 삭제되었습니다.`, "success");

      // ✅ invalidateQueries 사용
      queryClient.invalidateQueries({
        queryKey: ["stockInfoLog", group_code, project_code],
      });

      setRowSelection({});
    } catch (error) {
      console.error("Error deleting stock info:", error);
      openSnackbar("삭제 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEngineStatusClick = () => {
    if (engineStatus === 1 && !isScheduling) {
      handleOpenResetDialog();
    }
  };

  // ✅ 테이블 인스턴스
  const table = useMaterialReactTable({
    columns: stockColumns,
    data: filteredData || [],
    enableColumnFilters: true,
    enableColumnFilterModes: true,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableRowSelection: true,
    enableEditing: true,
    editDisplayMode: "row",
    onEditingRowSave: handleSaveRow,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading: isFetching,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,

    // ✅ Actions 컬럼 설정
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "",
        size: 100,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
      },
    },

    muiTableBodyCellProps: {
      sx: {
        fontSize: "14px",
        fontWeight: "500",
        padding: "10px !important",
        color: "#111",
      },
    },
    muiTableContainerProps: {
      sx: {
        width: "100%",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontSize: "15px !important",
        paddingBottom: "0.75rem",
        color: "#111",
        fontWeight: "600",
      },
    },
    muiTableHeadProps: {
      sx: {
        boxSizing: "border-box",
        "& tr": {
          boxSizing: "border-box",
          paddingBottom: 0,
          background: "#fff",
          color: "#111",
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        },
      },
    },
    muiTableBodyRowProps: {
      sx: {
        height: "60px",
        backgroundColor: "#fff",
        borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    },
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: false,
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    enablePagination: true,
    muiTablePaperProps: {
      sx: {
        boxShadow: "none !important",
        background: "none !important",
        borderBottom: "1px solid var(--mui-palette-divider)",
      },
    },
    enableTopToolbar: false,
  });

  const handleSchedulingClick = () => {
    const excludedOrderIds = table.options.data
      .filter(
        (row) =>
          row.is_excluded || (row.is_excluded === undefined && row.status === 3)
      )
      .map((row) => row.order_id);
    if (!data.length) {
      openSnackbar("주문을 추가한 후에 이용해 주세요.", "info");
      return;
    }
    if (
      excludedOrderIds.length === data.length &&
      excludedOrderIds.length &&
      data.length
    ) {
      openSnackbar("하나 이상의 주문을 선택해 주세요.", "error");
      return;
    }
    handleDoScheduling(
      selectedModel,
      data,
      excludedOrderIds,
      doSchedulingMutation,
      setSelectedModel,
      dateValue
    );
  };

  useEffect(() => {
    async function processCheck() {
      try {
        const res = await axios.get(
          TPC_URL +
            `/api/tpc/status?group_code=${group_code}&project_code=${project_code}`
        );
        const { status, isDirectionTwo, isGenerateNumber, rule_id } = res.data;
        const completeStatusParams = {
          isScheduling: false,
          processStatus: 0,
        };
        if (status.code === 9) {
          openSnackbar("일정계획 생성을 취소하였습니다.", "info");
          dispatch(handleSchedulingStatus(completeStatusParams));
          setLoading(false);
          setLoadingMessage("");
          return;
        }
        if (status.code === 3 || status.code === 0) {
          return;
        }
        if (status.code === 1) {
          setLoading(true);
          setLoadingMessage("일정계획 수행을 요청하였습니다.");
          const intervalId = setInterval(async () => {
            try {
              if (isCancelled.current) return;
              const statusRes = await axios.get(
                TPC_URL +
                  `/api/tpc/status?group_code=${group_code}&project_code=${project_code}`
              );
              const { status } = statusRes.data;
              if (status === 2) {
                setLoadingMessage("AI가 일정계획을 수립하는 중입니다.");
              }
              if (status.code === 3) {
                setLoading(false);
                setLoadingMessage("");
                dispatch(handleSchedulingStatus(completeStatusParams));
                setLoading(false);
                toast.success(
                  `일정계획 생성이 완료되었습니다. 소요시간 : ${statusRes.data.progress.formatted}`
                );
                clearInterval(intervalId);
              }
            } catch (e) {
              setLoading(false);
              clearInterval(intervalId);
            }
          }, 5000);
        }
        if (status.code === 2) {
          setLoadingMessage("AI가 일정계획을 수립하는 중입니다.");
          setLoading(true);
          const intervalId = setInterval(async () => {
            try {
              if (isCancelled.current) return;
              const statusRes = await axios.get(
                TPC_URL +
                  `/api/tpc/status?group_code=${group_code}&project_code=${project_code}`
              );
              const { status } = statusRes.data;
              if (status.code === 3) {
                setLoading(false);
                setLoadingMessage("");
                dispatch(handleSchedulingStatus(completeStatusParams));
                setLoading(false);
                toast.success(`일정계획 생성이 완료되었습니다.`);
                clearInterval(intervalId);
              }
            } catch (error) {
              console.error("Status check error:", error);
              clearInterval(intervalId);
              setLoading(false);
            }
          }, 5000);
        }
        if (status.code === 8) {
          setLoadingMessage("일정계획 취소를 요청하였습니다.");
          setLoading(true);
          const intervalId = setInterval(async () => {
            try {
              if (isCancelled.current) return;
              const statusRes = await axios.get(
                TPC_URL +
                  `/api/tpc/status?group_code=${group_code}&project_code=${project_code}`
              );
              const { status } = statusRes.data;
              if (status.code === 9) {
                openSnackbar("일정계획 생성을 취소하였습니다.", "info");
                setLoading(false);
                setLoadingMessage("");
                clearInterval(intervalId);
                dispatch(handleSchedulingStatus(completeStatusParams));
                return;
              }
            } catch (e) {
              clearInterval(intervalId);
              setLoading(false);
            }
          }, 5000);
        }
      } catch (e) {
        console.log(e);
      }
    }
    processCheck();
  }, [dispatch, group_code, project_code]);

  const schedBtnName =
    processStatus === 9
      ? "스케줄링 취소중"
      : processStatus === 1
      ? "스케줄링 취소"
      : "일정계획 수립";
  const schedBtnIcon =
    processStatus === 9 || processStatus === 1 ? (
      <StopIcon />
    ) : (
      <PlayArrowIcon />
    );
  const schedBtnColor =
    processStatus === 9 || processStatus === 1 ? "error" : "primary";

  return (
    <M.Wrapper>
      <M.FrameContainer>
        <M.ToolbarContainer>
          <M.ToolbarLeft>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ fontSize: "22px !important", color: "#111" }}
              >
                생산계획 수립
              </Typography>
            </div>
            <TextField
              placeholder="검색어를 입력하세요"
              size="small"
              value={searchText}
              onChange={onChangeSearchValue}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
                        {lastOperationTime && (
              <Chip
                label={`마지막 수립시간: ${lastOperationTime}`}
                color="info"
                size="small"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              />
            )}

            {/* ✅ 추가 버튼 */}
          </M.ToolbarLeft>
          <M.ToolbarCenter>
          <Button
              variant="contained"
              color={schedBtnColor}
              startIcon={schedBtnIcon}
              sx={{
                "&.Mui-disabled": {
                  opacity: 0.7,
                  background: "#a6a6a6",
                },
              }}
              onClick={
                handleGeneratePlan
              }
            >
             생산계획 수립
            </Button>
            <Button
              variant="contained"
              color={schedBtnColor}
              startIcon={schedBtnIcon}
              sx={{
                "&.Mui-disabled": {
                  opacity: 0.7,
                  background: "#a6a6a6",
                },
              }}
              disabled={processStatus === 9}
              onClick={
                !isScheduling ? handleSchedulingClick : handleCancelScheduling
              }
            >
              {schedBtnName}
            </Button>
          </M.ToolbarCenter>
          <M.ToolbarRight>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
              disabled={loading}
            >
              추가
            </Button>
              {Object.keys(rowSelection).length > 0 && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteRows}
                  disabled={loading}
                >
                  삭제 ({Object.keys(rowSelection).length})
                </Button>
              )}

              {scheOptions === 1 && (
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  disabled
                  sx={{
                    cursor: "default",
                    "&.Mui-disabled": {
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      opacity: 0.8,
                    },
                  }}
                >
                  엑셀 데이터 사용중
                </Button>
              )}

              <Button
                variant={engineStatus === 1 ? "outlined" : "contained"}
                color={engineStatus === 1 ? "warning" : "default"}
                onClick={handleEngineStatusClick}
                disabled={engineStatus === 0 || isScheduling}
                size="small"
                sx={{
                  cursor:
                    engineStatus === 1 && !isScheduling ? "pointer" : "default",
                  "&.Mui-disabled": {
                    opacity: 0.7,
                  },
                }}
              >
                {engineStatus === 0 ? "상태: 대기" : "상태: 동작중"}
              </Button>
            </div>
          </M.ToolbarRight>
        </M.ToolbarContainer>
        <M.InnerContainer>
          {loading && (
            <M.DimBox>
              <M.DimContent>
                <CirclesWithBar
                  visible={true}
                  height="100"
                  width="100"
                  color="#ffffff"
                  outerCircleColor="#ffffff"
                  innerCircleColor="#ffffff"
                  barColor="#4fa94d"
                  ariaLabel="circles-with-bar-loading"
                />
                <Typography variant="h6" color="white">
                  {loadingMessage || "데이터를 처리하는 중입니다..."}
                </Typography>
              </M.DimContent>
            </M.DimBox>
          )}
          <MaterialReactTable table={table} />
        </M.InnerContainer>
      </M.FrameContainer>

      {/* ✅ 엔진 초기화 다이얼로그 */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        aria-labelledby="reset-engine-dialog"
      >
        <DialogTitle id="reset-engine-dialog">엔진 초기화</DialogTitle>
        <DialogContent>
          <DialogContentText>엔진을 초기화하시겠습니까?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>취소</Button>
          <Button onClick={handleConfirmReset} color="error" autoFocus>
            초기화
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ 재고 정보 추가 모달 */}
      <CreateStockDialog
        open={addModalOpen}
        onClose={handleCloseAddModal}
        itemList={item || []}
        onSave={handleSaveNewStock}
        openSnackbar={openSnackbar}
      />

      {editQtyModalOpen && editQtyRowData && (
        <Modal
          open={editQtyModalOpen}
          onClose={handleCloseEditQtyModal}
          aria-labelledby="edit-qty-modal"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "400px",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <EditQtyDialog
              rowData={editQtyRowData}
              itemData={item}
              onClose={handleCloseEditQtyModal}
              handleUpdateOrder={handleUpdateOrder}
              openSnackbar={openSnackbar}
            />
          </Box>
        </Modal>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <BulkEditDialog
        open={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        selectedRows={table.getSelectedRowModel().rows}
        onSave={handleUpdateOrder}
        openSnackbar={openSnackbar}
        setRowSelection={setRowSelection}
      />
    </M.Wrapper>
  );
}

export default Plan;
